import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InternationalMetaTagsManager from './InternationalMetaTagsManager';
import { NavigationLink } from './Router';

interface FAQItem {
  question: string;
  answer: string;
  isOpen: boolean;
}

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Initialize FAQ items
    const items: FAQItem[] = [
      {
        question: t('faq.q1'),
        answer: t('faq.a1'),
        isOpen: false
      },
      {
        question: t('faq.q2'),
        answer: t('faq.a2'),
        isOpen: false
      },
      {
        question: t('faq.q3'),
        answer: t('faq.a3'),
        isOpen: false
      },
      {
        question: t('faq.q4'),
        answer: t('faq.a4'),
        isOpen: false
      },
      {
        question: t('faq.q5'),
        answer: t('faq.a5'),
        isOpen: false
      },
      {
        question: t('faq.q6'),
        answer: t('faq.a6'),
        isOpen: false
      },
      {
        question: t('faq.q7'),
        answer: t('faq.a7'),
        isOpen: false
      }
    ];

    setFaqItems(items);
  }, []);

  const toggleFAQ = (index: number) => {
    setFaqItems(prev => prev.map((item, i) => ({
      ...item,
      isOpen: i === index ? !item.isOpen : item.isOpen
    })));
  };

  return (
    <>
      <InternationalMetaTagsManager pageType="faq" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('faq.title')}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-200 dark:focus:bg-gray-700 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                    {item.question}
                  </h3>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${
                      item.isOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {item.isOpen && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Additional Help Section */}
          <div className="mt-12 bg-blue-900/50 border border-blue-700 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              If you couldn't find the answer you're looking for, feel free to explore our other resources or use our anonymous reporting system.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                {t('navigation.howTo')}
              </button>
              <button className="bg-gray-200 dark:bg-gray-700 text-blue-400 border border-blue-600 px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                {t('navigation.about')}
              </button>
            </div>
          </div>

          {/* Emergency Information */}
          <div className="mt-8 bg-red-50 dark:bg-red-900/50 border border-red-300 dark:border-red-700 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
                  Medical Emergency
                </h3>
                <p className="text-red-800 dark:text-red-200">
                  If you are experiencing severe food poisoning symptoms, seek immediate medical attention. Contact emergency services (112 in Belgium) or visit your nearest hospital emergency room.
                </p>
              </div>
            </div>
          </div>

          {/* Map CTA */}
          <div className="mt-8 text-center">
            <NavigationLink
              to="/"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium text-lg"
            >
              {t('faq.checkScore', "Check your restaurant's hygiene score")} &rarr;
            </NavigationLink>
          </div>

          {/* Keywords for SEO */}
          <div className="hidden">
            <span>{t('seo.keywords.foodSafety')}</span>
            <span>{t('seo.keywords.restaurantHygiene')}</span>
            <span>{t('seo.keywords.foodPoisoning')}</span>
            <span>{t('seo.keywords.belgiumRestaurants')}</span>
            <span>{t('seo.keywords.foodInspection')}</span>
            <span>FAQ food safety Belgium</span>
            <span>restaurant hygiene questions</span>
            <span>food poisoning help</span>
            <span>anonymous reporting FAQ</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;